const MULTI = "Select all that apply";
const ONE = "Choose one";

export const WELCOME = {
  title: "Get Started with Sabrea",
  body: "Tell us who you are, what you're working on, and what you need help with. Short and straightforward — your answers help us understand the right next steps for you.",
  time: "Estimated time: 3–5 minutes",
  links: "You can share music using links from Spotify, Apple Music, YouTube, SoundCloud, Dropbox, Google Drive, Audiomack, Bandcamp, private streaming links, or another platform.",
  note: "For questions with multiple options, select all that apply.",
  cta: "Get Started",
};

export const GENRES = [
  "Hip-hop / Rap", "R&B", "Soul", "Pop", "K-pop", "J-pop", "Afrobeats", "Amapiano", "Reggaeton",
  "Latin", "Reggae / Dancehall", "Drill", "Trap", "House", "Techno", "Electronic", "Dance",
  "Alternative", "Indie", "Rock", "Metal", "Punk", "Country", "Folk", "Americana", "Jazz",
  "Blues", "Funk", "Gospel", "Christian", "Classical", "Musical theatre", "Desi / South Asian",
  "Arabic / MENA", "Singer-songwriter", "Experimental", "Instrumental / Beats", "Other",
];

export const SUPPORT_OPTIONS = [
  "Artist development", "Music production", "Engineers / recording support", "Studio access",
  "Mixing / mastering", "Songwriting development", "Vocal coaching", "Live performance development",
  "Branding / creative direction", "Marketing", "Content strategy", "Music release planning",
  "Artist management", "Music business guidance", "Administrative support",
  "Networking / industry relationships", "Events / showcases",
];

export const STEPS = [
  {
    id: "about", label: "A", title: "About You", blurb: "The basics — who you are and how we reach you.",
    fields: [
      { id: "roles", label: "How do you currently identify creatively?", mode: MULTI, type: "multiselect", required: true,
        options: ["Artist / Recording Artist", "Singer", "Rapper", "Songwriter", "Producer", "Musician", "DJ", "Engineer", "Dancer / Performer", "Content creator", "Other"] },
      { id: "full_name", label: "What's your name?", type: "text", required: true },
      { id: "release_name", label: "What name do you create or release music under?", type: "text", required: true },
      { id: "email", label: "Email address", type: "email", required: true },
      { id: "phone", label: "Phone number", type: "text", required: true },
      { id: "location", label: "Where are you based?", help: "City + state/country.", type: "text", required: true },
      { id: "age_band", label: "What age range are you in?", mode: ONE, type: "select", required: true, options: ["Under 18", "18–24", "25–34", "35+"] },
      { id: "guardian_name", label: "Guardian contact name", type: "text", required: true, cond: (a) => a.age_band === "Under 18" },
      { id: "guardian_email", label: "Guardian contact email", type: "email", required: true, cond: (a) => a.age_band === "Under 18" },
    ],
  },
  {
    id: "music", label: "B", title: "Your Music", blurb: "Your sound and where we can hear it.",
    fields: [
      { id: "genres", label: "What genres do you create in?", mode: MULTI, type: "multiselect", required: true, options: GENRES },
      { id: "music_links", label: "Share any music you'd like us to hear", type: "links", required: true,
        help: "Spotify, Apple Music, YouTube, SoundCloud, Audiomack, Bandcamp, Dropbox, Google Drive, private links — add as many as you like." },
      { id: "socials", label: "Social media", type: "pairs", addLabel: "Add Another Social Profile",
        options: ["Instagram", "TikTok", "YouTube", "X / Twitter", "Threads", "Facebook", "Spotify", "Apple Music", "SoundCloud", "Twitch", "Other"] },
    ],
  },
  {
    id: "support", label: "C", title: "What You Need", blurb: "Where Sabrea can actually help.",
    fields: [
      { id: "looking_for", label: "What do you need help with?", mode: MULTI, type: "multiselect", required: true, options: SUPPORT_OPTIONS },
      { id: "support_needed", label: "How would you like Sabrea to support you?", type: "longtext", required: true,
        help: "Artist development, music production, marketing, management, business support — describe it in your own words." },
    ],
  },
  {
    id: "goals", label: "D", title: "Your Goals", blurb: "In your own words. There are no wrong answers.",
    fields: [
      { id: "dream", label: "What is your dream?", type: "longtext", required: true,
        help: "Where do you want this to go? Think bigger than the next release." },
      { id: "current_focus", label: "What are you currently focused on as an artist?", type: "longtext", required: true },
      { id: "creative_improve", label: "What are you trying to improve most creatively?", type: "longtext", required: true },
      { id: "anything_else", label: "Is there anything else we should know that would help us support you?", help: "Optional.", type: "longtext" },
    ],
  },
  {
    id: "budget", label: "E", title: "Your Budget", blurb: "The last question — it helps us recommend what actually fits.",
    fields: [
      { id: "budget_range", label: "What range are you able to invest in your development right now?", type: "range", required: true,
        min: 0, max: 30000, step: 500,
        help: "Drag both handles to set your range. This is only used to shape realistic recommendations — nothing is charged and nothing is committed.",
        bands: [
          [0, 1000, "Usually covers focused, single-outcome work — one song, a coaching block, or a targeted piece of development."],
          [1000, 2000, "Typically a single-song package: production, mix and master, or a short development sprint."],
          [2000, 5000, "Room for a small body of work — a few songs, or development paired with content and release prep."],
          [5000, 10000, "A project-level scope: an EP-sized body of work, or development plus brand and marketing support."],
          [10000, 30000, "Multi-track projects with creative direction, visuals, marketing and longer-term development."],
        ] },
    ],
  },
];

export const visibleSteps = (answers) => STEPS.filter((s) => !s.cond || s.cond(answers));
export const visibleFields = (step, answers) => step.fields.filter((f) => !f.cond || f.cond(answers));

export const SUCCESS_METRICS = ["Songs finished", "Songs released", "Saves per listener", "Repeat listeners", "Email/SMS list growth", "Show attendance", "Sessions completed", "Collaborations made", "Content output consistency", "Skill milestones"];
export const OBJECTIONS = ["Money", "Timing", "Trust", "Scope"];
